import React, { useEffect, useRef } from 'react';
import './Home.css';
import googlePlay from '../assets/google-play-store logo.png';
import appStore from '../assets/app-store-logo.png';
import Footer from './Footer';
import ChatSimulation from './ChatSimulation';
import {
  FaLock,
  FaPhoneAlt,
  FaCheckDouble,
  FaUsers,
  FaCloud,
  FaGlobeAmericas,
} from 'react-icons/fa';

const FEATURES = [
  {
    icon: <FaLock />,
    title: 'End-to-end encryption',
    text: 'Messages are locked before they leave your device — only the recipient can read them.',
  },
  {
    icon: <FaPhoneAlt />,
    title: 'Audio & video calls',
    text: 'Crystal-clear calls that stay just as private as your chats.',
  },
  {
    icon: <FaCheckDouble />,
    title: 'Read receipts',
    text: 'Know when a message lands — toggle it off any time you want.',
  },
  {
    icon: <FaUsers />,
    title: 'Communities',
    text: 'Build groups and spaces without limits, wherever your people are.',
  },
  {
    icon: <FaCloud />,
    title: 'Encrypted backups',
    text: 'Keep your history safe without handing it to anyone else.',
  },
  {
    icon: <FaGlobeAmericas />,
    title: 'Privacy-first design',
    text: 'No ad-mining of your conversations. Ever.',
  },
];

function useRevealOnScroll() {
  const containerRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const cards = el.querySelectorAll('.reveal');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    cards.forEach((c) => io.observe(c));
    return () => io.disconnect();
  }, []);

  return containerRef;
}

function Home() {
  const featureGridRef = useRevealOnScroll();

  return (
    <div className="home-page">
      <div className="home-container">
        <header className="hero-section">
          <div className="hero-content">
            <div className="eyebrow">
              <span className="dot" />
              END-TO-END ENCRYPTED
            </div>

            <h1>
              Talk without <span className="accent-text">the trade-off.</span>
            </h1>

            <p className="hero-subtitle">
              Lutero pairs private messaging with calls, video, and communities —
              so nothing about how you connect gets watched, mined, or sold.
            </p>

            <div className="store-links">
              <a href="https://apps.apple.com/us/app/lutero/id6749874059">
                <img src={appStore} alt="App Store" />
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.elexistech.lutero">
                <img src={googlePlay} alt="Google Play" />
              </a>
            </div>

            <div className="trust-row">
              <span><FaLock size={11} /> E2E ENCRYPTION</span>
              <span><FaCheckDouble size={11} /> READ RECEIPTS</span>
              <span><FaCloud size={11} /> ENCRYPTED BACKUPS</span>
            </div>
          </div>

          <ChatSimulation />
        </header>

        <section className="core-section" ref={featureGridRef}>
          <div className="section-head">
            <div className="section-eyebrow">CORE FEATURES</div>
            <h2>Built for private, reliable connection</h2>
            <p className="section-desc">
              Every conversation on Lutero is protected the same way, by default —
              no settings to dig for.
            </p>
          </div>

          <div className="feature-grid">
            {FEATURES.map((f) => (
              <div className="feature-card reveal" key={f.title}>
                <div className="feature-icon">{f.icon}</div>
                <h3>{f.title}</h3>
                <p>{f.text}</p>
              </div>
            ))}
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}

export default Home;